import { useState, useEffect, useContext, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Modal, Box, TextField, MenuItem, Button } from '@mui/material';
import { authFetch } from '../utils/api';
import { formInputSx } from '../styles/inputStyles';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner, faCircleInfo, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faInstagram, faWhatsapp, faTelegram, faSlack } from '@fortawesome/free-brands-svg-icons';
import SlackLogo from './SlackLogo';
import NotionLogo from './NotionLogo';
import { useTranslation } from 'react-i18next';
import './ManageConnectionsModal.css';
import '../translations.js';
import ConfirmationModal from './confirmationModal';
import { guardWrite } from '../utils/permissions';
import TelegramDiagnostics from './TelegramDiagnostics';
import SocialDeliveryError from './SocialDeliveryError';
import ManualReplyPauseSetting from './ManualReplyPauseSetting';
import { ChannelModalContext } from '../contexts/ChannelModalContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';

const settingValue = (key, value) => key === 'pause_on_manual_reply' ? value !== false
  : key === 'auto_response_mode' ? value || 'respond_all' : value || null;

// Google Drive SVG Logo Component
const GoogleDriveLogo = ({ size = 24, style = {} }) => (
  <svg width={size} height={size * 0.89} viewBox="0 0 87.3 78" style={style}>
    <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/>
    <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"/>
    <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"/>
    <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/>
    <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/>
    <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/>
  </svg>
);

const ManageConnectionsModal = ({ open, onClose, connections, onUpdate, platform, connectionType = 'messaging', inline = false, botId = null }) => {
  // connectionType: 'messaging' for social media, 'data' for data sources like Google Drive
  const [localConnections, setLocalConnections] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const hasChanges = Object.keys(pendingChanges).length > 0;
  const baseline = useRef([]);
  const pendingRef = useRef(pendingChanges);
  pendingRef.current = pendingChanges;
  const savingRef = useRef(false);
  const handlersRef = useRef({});
  const guardId = useId();
  const modalFrame = useContext(ChannelModalContext);
  const { registerUnsavedChangesGuard, requestUnsavedAction } = useUnsavedChanges();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine API endpoint based on connection type. Wix isn't an OAuth
  // data-connection (it's per-agent, installed via the App Market), so its delete
  // routes to the bot-scoped wix/disconnect endpoint — same modal + same DELETE,
  // just a different resource.
  const getApiEndpoint = (id = '') => {
    if (platform === 'wix') {
      return `${process.env.REACT_APP_API_BASE_URL}wix/disconnect?bot_id=${encodeURIComponent(botId || '')}`;
    }
    // Shopify, like Wix, is a per-agent platform install rather than an OAuth
    // data-connection — but its disconnect takes the bot in a POST body.
    if (platform === 'shopify') {
      return `${process.env.REACT_APP_API_BASE_URL}apps/shopify/disconnect`;
    }
    if (connectionType === 'data') {
      return `${process.env.REACT_APP_API_BASE_URL}data-connections${id ? `/${id}` : ''}`;
    }
    return `${process.env.REACT_APP_API_BASE_URL}messaging_connections${id ? `/${id}` : ''}`;
  };

  // Everything here removes a connection; only Shopify's route spells it POST.
  const getDeleteRequest = () => (platform === 'shopify'
    ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bot_id: botId }) }
    : { method: 'DELETE' });
  
  const isDataConnection = connectionType === 'data';
  const [resuming, setResuming] = useState(null);
  const [resumeError, setResumeError] = useState(false);
  const resumeConnection = async (connection) => {
    if (!guardWrite() || resuming) return;
    setResuming(connection.id);
    setResumeError(false);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}mcp-configuration/actions/channel_change`, {
        method: 'POST', body: JSON.stringify({ agent_id: connection.bot_id, connection_id: connection.id,
          expected_status: 'paused', action: 'activate', idempotency_key: crypto.randomUUID() }),
      });
      if (response?.ok) {
        setLocalConnections(previous => previous.map(item => item.id === connection.id ? { ...item, status: 'active' } : item));
        if (onUpdate) onUpdate();
      } else { setResumeError(true); }
    } catch { setResumeError(true); }
    finally { setResuming(null); }
  };

  useEffect(() => {
    if (open) {
      baseline.current = connections;
      // A refresh after activation/deletion must not erase other unsaved edits.
      setLocalConnections(connections.map(conn => ({ ...conn, ...pendingRef.current[conn.id] })));
      fetchBots();
    } else {
      setPendingChanges({});
      setSaveError(null);
    }
  }, [open, connections]);

  const fetchBots = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id") || ''}&include_knowledge=False`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const completedBots = data.filter((bot) => bot.status === 'completed');
      setBots(completedBots);
    } catch (err) {
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  // Keep only actual differences, including default values on older connections.
  const queueChange = (connectionId, patch) => {
    setLocalConnections(prev =>
      prev.map(conn =>
        conn.id === connectionId ? { ...conn, ...patch } : conn
      )
    );

    setPendingChanges(prev => {
      const original = baseline.current.find(conn => conn.id === connectionId) || {};
      const changed = { ...(prev[connectionId] || {}), ...patch };
      Object.keys(changed).forEach(key => {
        if (settingValue(key, changed[key]) === settingValue(key, original[key])) delete changed[key];
      });
      const next = { ...prev };
      if (Object.keys(changed).length) next[connectionId] = changed;
      else delete next[connectionId];
      return next;
    });
    setSaveError(null);
  };

  const handleBotChange = (connectionId, newBotId) => {
    queueChange(connectionId, { bot_id: newBotId });
  };

  const handleAutoResponseModeChange = (connectionId, newMode) => {
    queueChange(connectionId, { auto_response_mode: newMode });
  };

  const handleSaveChanges = async () => {
    if (savingRef.current) return false;
    if (!hasChanges) return true;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      // Save all pending changes. Each entry can carry bot_id, auto_response_mode, or both.
      const entries = Object.entries(pendingChanges);
      const savePromises = entries.map(([connectionId, patch]) => {
        const body = {};
        if ('bot_id' in patch) body.bot_id = patch.bot_id || null;
        if ('auto_response_mode' in patch) body.auto_response_mode = patch.auto_response_mode;
        if ('pause_on_manual_reply' in patch) body.pause_on_manual_reply = patch.pause_on_manual_reply;

        return fetch(
          getApiEndpoint(connectionId),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        ).then(async response => {
          if (!response.ok) {
            const result = await response.json();
            throw new Error(result.detail || 'Failed to update connection');
          }
          return response.json();
        });
      });

      // Wait for every request even if one fails, before allowing another save.
      const results = await Promise.allSettled(savePromises);
      const saved = new Set(entries.filter((_, index) => results[index].status === 'fulfilled').map(([id]) => id));
      baseline.current = baseline.current.map(conn => saved.has(String(conn.id))
        ? { ...conn, ...pendingChanges[conn.id] } : conn);
      setPendingChanges(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => !saved.has(id))));
      const failed = results.find(result => result.status === 'rejected');
      if (failed) throw failed.reason;

      // Clear pending changes
      setPendingChanges({});
      baseline.current = localConnections;
      onUpdate();
      return true;
    } catch (error) {
      console.error('Error saving changes:', error);
      const message = error.message || t('unsaved_save_failed');
      setSaveError(message);
      return message;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDeleteConnection = async () => {
    if (!connectionToDelete) return;

    try {
      const connectionId = connectionToDelete.id;
      const req = getDeleteRequest();
      const response = await fetch(
        getApiEndpoint(connectionId),
        {
          ...req,
          headers: {
            ...(req.headers || {}),
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete connection');
      }

      // Update local state
      setLocalConnections(prev => prev.filter(conn => conn.id !== connectionId));
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next[connectionId];
        return next;
      });
      onUpdate();
      setConnectionToDelete(null);
    } catch (error) {
      console.error('Error deleting connection:', error);
      alert('Failed to delete connection');
    }
  };

  // WhatsApp only: re-run subscribe + register on the backend. Needed after
  // Meta approves the display name of a Meta-provided (+1 555) number — the
  // approved name is applied on re-registration and only then can it send.
  const [activatingId, setActivatingId] = useState(null);
  const handleReactivate = async (connection) => {
    if (!guardWrite()) return;
    setActivatingId(connection.id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}messaging_connections/${connection.id}/activate`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to re-activate connection');
      }
      const a = data.activation || {};
      if (a.subscribed && a.registered) {
        alert(t('connection_reactivate_ok', { status: a.phone_status || '', name_status: a.name_status || '' }));
      } else {
        alert(t('connection_reactivate_failed', { error: (a.errors || []).join('; ') }));
      }
      onUpdate();
    } catch (error) {
      console.error('Error re-activating connection:', error);
      alert(t('connection_reactivate_failed', { error: error.message }));
    } finally {
      setActivatingId(null);
    }
  };

  const openDeleteConfirmation = (connection) => {
    // Pre-gate: a viewer would otherwise walk through the whole confirm dialog
    // only to hit a 403 at the end. guardWrite surfaces the unified denied modal.
    if (!guardWrite()) return;
    setConnectionToDelete(connection);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setConnectionToDelete(null);
  };

  const getPlatformDisplayName = (platform) => {
    const platformNames = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      slack: 'Slack',
      google_drive: 'Google Drive',
      onedrive: 'OneDrive',
      notion: 'Notion',
      wix: 'Wix',
      shopify: 'Shopify',
    };
    return platformNames[platform] || platform;
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      facebook: faFacebook,
      instagram: faInstagram,
      whatsapp: faWhatsapp,
      telegram: faTelegram,
      slack: faSlack,
      google_drive: null, // Uses SVG component instead
    };
    return platformIcons[platform?.toLowerCase()] || faFacebook;
  };

  const getPlatformColor = (platform) => {
    const platformColors = {
      facebook: '#1877f2',
      instagram: '#E4405F',
      whatsapp: '#25D366',
      telegram: '#229ED9',
      slack: '#2EB67D',
      google_drive: '#4285f4',
    };
    return platformColors[platform?.toLowerCase()] || '#5616ea';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const userDateFormat = localStorage.getItem("date_format") || "YYYY-MM-DD";

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());

    if (userDateFormat === "DD-MM-YYYY") {
      return `${day}-${month}-${year}`;
    } else if (userDateFormat === "MM-DD-YYYY") {
      return `${month}-${day}-${year}`;
    } else {
      return `${year}-${month}-${day}`;
    }
  };

  // Inline mode (rendered inside the Channels modal): flat, full-width, no
  // panel chrome / background / own padding — the host modal provides those.
  handlersRef.current = {
    save: handleSaveChanges,
    close: onClose,
    discard: () => {
      setLocalConnections(baseline.current);
      setPendingChanges({});
      setSaveError(null);
    },
  };
  useEffect(() => {
    if (!open || isDataConnection) return undefined;
    return registerUnsavedChangesGuard(guardId, {
      when: hasChanges,
      onSave: () => handlersRef.current.save(),
      onDiscard: () => handlersRef.current.discard(),
    });
  }, [open, isDataConnection, hasChanges, guardId, registerUnsavedChangesGuard]);

  const setNavigation = modalFrame?.setNavigation;
  useEffect(() => {
    if (!open || !inline || !setNavigation) return undefined;
    setNavigation({ onBack: () => handlersRef.current.close(), busy: saving });
    return () => setNavigation(null);
  }, [open, inline, setNavigation, saving]);

  const requestClose = () => {
    if (!savingRef.current) requestUnsavedAction(onClose);
  };
  const panelSx = inline
    ? { display: 'flex', flexDirection: 'column', width: '100%', outline: 'none' }
    : {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'white',
        borderRadius: '10px',
        boxShadow: 24,
        width: isMobile ? '80%' : '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
      };
  const headerSx = inline
    ? { flexShrink: 0, pb: 1.5 }
    : {
        flexShrink: 0,
        px: isMobile ? 2.5 : 2.75,
        pt: isMobile ? 2.5 : 2.75,
        pb: 2,
        borderBottom: '1px solid #f0f0f0',
      };
  const contentSx = inline
    ? { pt: 1 }
    : {
        px: isMobile ? 2.5 : 2.75,
        pt: 2,
        pb: isMobile ? 2.5 : 2.75,
        overflowY: 'auto',
        minHeight: 0,
      };

  const footer = <div className="connection-editor-footer">
    {saveError && <Alert severity="error" sx={{ mb: 1.5 }}>{saveError}</Alert>}
    <div className="connection-editor-actions">
      {!inline && <Button onClick={requestClose} disabled={saving} variant="outlined"
        sx={{ color: '#5616ea', borderColor: '#5616ea' }}>{t('close')}</Button>}
      {!isDataConnection && <Button onClick={handleSaveChanges}
        disabled={!hasChanges || saving || loading} variant="contained"
        startIcon={saving ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}
        sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}>
        {t('save')}
      </Button>}
    </div>
  </div>;

  const panel = (
      <Box sx={panelSx}>
        <Box sx={headerSx}>
          <h3 className="manage-connections-modal-title" style={{ marginBottom: 0 }}>
            {platform
              ? t('manage_connections_platform_title', { platform: getPlatformDisplayName(platform) })
              : t('manage_connections_title')}
          </h3>
        </Box>

        <Box sx={contentSx}>
        {resumeError && <Alert severity="error">{t('mcp_continue_invalid')}</Alert>}
        <div className="connection-info-box">
          <FontAwesomeIcon className="connection-info-icon" icon={faCircleInfo} />
          <div className="connection-info-content">
            <p>
              {isDataConnection
                ? t('manage_connections_data_info')
                : t('manage_connections_messaging_info')
              }
            </p>
          </div>
        </div>

        {loading && !isDataConnection ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#5616ea' }} />
          </div>
        ) : !isDataConnection && bots.length === 0 ? (
          <div className="empty-state">
            <p>{t('manage_connections_no_bots')}</p>
          </div>
        ) : localConnections.length === 0 ? (
          <div className="empty-state">
            <p>{t('manage_connections_empty')}</p>
          </div>
        ) : (
          <div className="connections-grid">
            {localConnections.map((connection) => (
              <div key={connection.id} className="connection-card">
                <div className="connection-header">
                  <h4 className="connection-platform-name">
                    {connection.platform === 'google_drive' ? (
                      <GoogleDriveLogo size={22} style={{ marginRight: '10px', flexShrink: 0 }} />
                    ) : connection.platform === 'onedrive' ? (
                      <img src="/onedrive.png" alt="OneDrive" style={{ height: '22px', width: 'auto', marginRight: '10px', flexShrink: 0 }} />
                    ) : connection.platform === 'notion' ? (
                      <NotionLogo size={22} style={{ marginRight: '10px', flexShrink: 0 }} />
                    ) : connection.platform === 'wix' ? (
                      <img src="/wix-logo.png" alt="Wix" style={{ height: '22px', width: 'auto', marginRight: '10px', flexShrink: 0 }} />
                    ) : connection.platform === 'shopify' ? (
                      <img src="/shopify-logo.svg" alt="Shopify" style={{ height: '24px', width: 'auto', marginRight: '10px', flexShrink: 0 }} />
                    ) : connection.platform === 'slack' ? (
                      /* Slack's brand mark is intentionally 4-color (red,
                         blue, green, yellow). Other channels keep the
                         monochrome FA glyph in their brand color. */
                      <SlackLogo size={22} style={{ marginRight: '10px', flexShrink: 0 }} />
                    ) : (
                      <FontAwesomeIcon
                        icon={getPlatformIcon(connection.platform)}
                        style={{ marginRight: '10px', color: getPlatformColor(connection.platform), flexShrink: 0 }}
                      />
                    )}
                    <span className="connection-platform-name-text">
                      {connection.display_name || connection.external_id || t('connected_account_fallback')}
                    </span>
                  </h4>
                  <button
                    className="connection-delete-btn"
                    disabled={saving}
                    onClick={() => openDeleteConfirmation(connection)}
                    aria-label={t('delete_connection_aria')}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
                {!isDataConnection && !connection.bot_id && connection.status !== 'token_expired' && (
                  <div className="unassigned-badge">
                    {t('connection_not_assigned')}
                  </div>
                )}

                {/* Most recent rejected provider send (backend `last_error`).
                    Shows WHY a connected channel stays silent, e.g. WhatsApp
                    131037 "display name needs approval". */}
                {connection.status !== 'token_expired' && connection.last_error && (
                  connection.last_error.category ? <SocialDeliveryError error={connection.last_error} /> :
                  <div className="unassigned-badge" title={connection.last_error.at || ''}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                    {t('connection_last_send_error', {
                      code: connection.last_error.code ?? connection.last_error.http_status ?? '',
                      message: connection.last_error.message || '',
                    })}
                  </div>
                )}

                {connection.platform === 'telegram' && <TelegramDiagnostics connectionId={connection.id} />}

                {connection.platform === 'whatsapp' && connection.status !== 'token_expired' && (
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={saving || activatingId === connection.id}
                      onClick={() => handleReactivate(connection)}
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                      }}
                    >
                      {activatingId === connection.id
                        ? <FontAwesomeIcon icon={faSpinner} spin />
                        : t('connection_reactivate')}
                    </Button>
                  </div>
                )}

                {connection.status === 'paused' && <Alert severity="info" sx={{ mt: 1 }}
                  action={<Button color="inherit" disabled={!!resuming} onClick={() => resumeConnection(connection)}>{t('mcp_channel_resume')}</Button>}>
                  {t('mcp_channel_paused')}
                </Alert>}
                {connection.status === 'token_expired' && (
                  <div className="expired-reconnect-bar">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="expired-icon" />
                    <span style={{ flex: 1 }}>{t('connection_expired_message')}</span>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving}
                      onClick={() => requestUnsavedAction(() => {
                        onClose();
                        // Trigger reconnect by navigating — parent handles the OAuth flow
                        window.dispatchEvent(new CustomEvent('reconnect-platform', { detail: { platform: connection.platform, botId: connection.bot_id } }));
                      })}
                      sx={{
                        backgroundColor: '#e53935',
                        '&:hover': { backgroundColor: '#c62828' },
                        fontSize: '0.9rem',
                        textTransform: 'none',
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('reconnect')}
                    </Button>
                  </div>
                )}

                {!isDataConnection && (
                  <div className="connection-editable">
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label={t('assign_bot_label')}
                      disabled={saving}
                      value={connection.bot_id || ''}
                      onChange={(e) => handleBotChange(connection.id, e.target.value)}
                      className="user-settings-input"
                      sx={formInputSx}
                    >
                      <MenuItem value="">
                        <em>{t('no_bot_assigned')}</em>
                      </MenuItem>
                      {bots.map((bot) => (
                        <MenuItem key={bot.id} value={bot.id}>
                          {bot.name || bot.domain_name || bot.id}
                        </MenuItem>
                      ))}
                    </TextField>

                    <ManualReplyPauseSetting
                      platform={connection.platform || platform}
                      checked={connection.pause_on_manual_reply !== false}
                      onChange={(checked) => queueChange(connection.id, { pause_on_manual_reply: checked })}
                      disabled={saving}
                    />

                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label={t('reply_behavior_label')}
                      disabled={saving}
                      value={connection.auto_response_mode || 'respond_all'}
                      onChange={(e) => handleAutoResponseModeChange(connection.id, e.target.value)}
                      className="user-settings-input"
                      sx={formInputSx}
                    >
                      <MenuItem value="opt_in">{t('reply_mode_opt_in')}</MenuItem>
                      <MenuItem value="respond_all">{t('reply_mode_respond_all')}</MenuItem>
                    </TextField>

                    <div className="connection-mode-help">
                      {(connection.auto_response_mode || 'respond_all') === 'opt_in'
                        ? t('reply_mode_opt_in_help')
                        : t('reply_mode_respond_all_help')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        </Box>

        {inline && modalFrame ? (modalFrame.footerTarget && open && createPortal(footer, modalFrame.footerTarget)) : footer}

        <ConfirmationModal
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirmation}
          onConfirm={handleDeleteConnection}
          itemName={connectionToDelete?.display_name || connectionToDelete?.external_id || t('this_connection_fallback')}
          requireTextConfirmation={false}
        />
      </Box>
  );

  if (inline) {
    return open ? <div className="manage-connections-inline">{panel}</div> : null;
  }

  return <Modal open={open} onClose={requestClose}>{panel}</Modal>;
};

export default ManageConnectionsModal;
